// src/components/AIChatAssistant.tsx

import { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ScrollArea } from './ui/scroll-area';
import { Bot, User, X, Send, CornerDownLeft } from 'lucide-react';
import { Badge } from './ui/badge';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// The API_BASE remains, as it's specific to this component's functionality
const API_BASE = "http://127.0.0.1:4000/coliving-ai-os/chat-analysis/dashboard";

interface ChatMessage {
  id: string;
  content: string;
  isFromUser: boolean;
  timestamp: Date;
}

export function AIChatAssistant() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      content: "Hello! I'm here to help. You can ask me about ticket trends, customer sentiment, or agent performance.",
      isFromUser: false,
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Auto-scroll to the bottom when new messages are added
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTo({
        top: scrollAreaRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages]);

  const handleSendMessage = async (messageText: string) => {
    const text = messageText.trim();
    if (!text || isLoading) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      content: text,
      isFromUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const url = `${API_BASE}/mini-ai-agent-v4`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: text })
      });

      if (!response.ok || !response.body) {
        throw new Error(`HTTP ${response.status}: Network response was not ok`);
      }

      const aiMessageId = (Date.now() + 1).toString();
      let accumulatedText = '';
      setMessages(prev => [
        ...prev,
        {
          id: aiMessageId,
          content: '',
          isFromUser: false,
          timestamp: new Date()
        }
      ]);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        accumulatedText += decoder.decode(value, { stream: true });
        setMessages(prev =>
          prev.map(m =>
            m.id === aiMessageId ? { ...m, content: accumulatedText } : m
          )
        );
      }
    } catch (error) {
      console.error('Failed to get AI response:', error);
      setMessages(prev => [
        ...prev,
        {
          id: (Date.now() + 2).toString(),
          content: "Sorry, I encountered an error. Please check the console or try again later.",
          isFromUser: false,
          timestamp: new Date()
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePromptClick = (prompt: string) => {
    handleSendMessage(prompt);
  };
  
  const quickPrompts = [
    "Summarize yesterday's sentiment",
    "Any urgent tickets?",
    "Top 3 trending topics this week",
    "Which agent has the most resolved cases?",
  ];

  return (
    <div className="fixed top-6 right-6 z-50">
      {!isOpen ? (
        <Button
          onClick={() => setIsOpen(true)}
          className="h-14 w-14 rounded-full shadow-lg hover:scale-105 transition-transform bg-gradient-to-br from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
          size="icon"
        >
          <Bot className="h-6 w-6 text-white" />
        </Button>
      ) : (
        <Card className="w-96 h-[600px] flex flex-col shadow-2xl rounded-2xl border-gray-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 border-b">
            <div className='flex items-center gap-2'>
              <Bot className="h-5 w-5 text-purple-600" />
              <CardTitle className="text-md font-semibold">AI Assistant</CardTitle>
            </div>
            <Button 
              variant="ghost" 
              size="icon"
              className="h-7 w-7 rounded-full"
              onClick={() => setIsOpen(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </CardHeader>
          
          <CardContent className="p-0 flex-1 flex flex-col">
            <ScrollArea className="flex-1 p-4" ref={scrollAreaRef}>
              <div className="space-y-4">
                {messages.map((message, index) => (
                  <div key={message.id} className={`flex items-start gap-3 ${message.isFromUser ? 'justify-end' : ''}`}>
                    {!message.isFromUser && (
                      <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                        <Bot className="h-5 w-5 text-gray-600" />
                      </div>
                    )}
                    <div className={`max-w-[85%] p-3 rounded-lg text-sm prose prose-sm prose-p:my-0 ${
                      message.isFromUser 
                        ? 'bg-blue-500 text-white rounded-br-none' 
                        : 'bg-gray-100 text-gray-800 rounded-bl-none'
                    }`}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {message.content}
                      </ReactMarkdown>
                      {isLoading && !message.isFromUser && index === messages.length - 1 && (
                         <span className="ml-1 inline-block w-2 h-4 bg-current animate-pulse" />
                      )}
                    </div>
                     {message.isFromUser && (
                      <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                        <User className="h-5 w-5 text-gray-600" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
            
            <div className="p-4 border-t bg-gray-50/50 rounded-b-2xl">
              <div className="mb-3">
                  <p className="text-xs font-medium text-gray-600 mb-2">Quick Prompts</p>
                  <div className="flex flex-wrap gap-2">
                    {quickPrompts.map(prompt => (
                      <Badge 
                        key={prompt} 
                        variant="outline" 
                        className="text-xs cursor-pointer hover:bg-gray-200"
                        onClick={() => handlePromptClick(prompt)}
                      >
                        {prompt}
                      </Badge>
                    ))}
                  </div>
              </div>
              <div className="flex gap-2 items-center">
                <Input
                  placeholder="Ask a question..."
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage(inputMessage)}
                  className="text-sm bg-white"
                  disabled={isLoading} 
                />
                <Button 
                  size="icon"
                  onClick={() => handleSendMessage(inputMessage)}
                  className="flex-shrink-0"
                  disabled={isLoading || !inputMessage.trim()} 
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}